import { auth, db, storage } from './firebase';
import { 
  collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, 
  query, where, orderBy as firestoreOrderBy, limit as firestoreLimit 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';

class FirebaseAdapter {
  constructor() {
    this.auth = {
      me: () => new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          unsubscribe();
          if (user) {
            let userData = { id: user.uid, email: user.email, name: user.displayName };
            try {
              // Chercher le rôle admin dans la collection 'User' (Majuscule) ou 'users'
              let firestoreUser = null;
              
              const userRefCap = collection(db, 'User');
              const qCap = query(userRefCap, where('email', '==', user.email));
              const snapCap = await getDocs(qCap);
              
              if (!snapCap.empty) {
                firestoreUser = snapCap.docs[0].data();
              } else {
                const userRefLow = collection(db, 'users');
                const qLow = query(userRefLow, where('email', '==', user.email));
                const snapLow = await getDocs(qLow);
                if (!snapLow.empty) firestoreUser = snapLow.docs[0].data();
              }
              
              if (firestoreUser) {
                userData.admin_role = firestoreUser.admin_role;
                userData.full_name = firestoreUser.full_name;
              }
            } catch (err) {
              console.error("Erreur lecture rôle admin:", err);
            }

            // AUTO-FIX (Même en cas d'erreur de permission Firestore)
            if (!userData.admin_role && user.email === 'admin@r2bentreprises.com') {
              userData.admin_role = 'principal';
              userData.full_name = 'Administrateur R2B';
            }

            resolve(userData);
          } else {
            reject(new Error('API Error: 404')); // Simulate old base44 response when not logged in
          }
        });
      }),
      login: async (email, password) => {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return { token: userCredential.user.accessToken };
      },
      logout: () => signOut(auth),
      updateMe: async (data) => {
        if (auth.currentUser && data.name) {
          await updateProfile(auth.currentUser, { displayName: data.name });
        }
        return { id: auth.currentUser?.uid, email: auth.currentUser?.email, name: data.name };
      },
    };
    this.entities = this._createEntitiesProxy();
    this.integrations = {
      Core: {
        SendEmail: async (data) => {
          console.log('Mock SendEmail with Firebase', data);
          return { success: true };
        },
        UploadFile: async (data) => this._uploadFile(data.file),
      }
    };
  }

  async _uploadFile(file) {
    const storageRef = ref(storage, `uploads/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return { url };
  }

  _createEntitiesProxy() {
    const createEntityClient = (entityName) => ({
      list: async (orderByField) => {
        const q = orderByField 
          ? query(collection(db, entityName), firestoreOrderBy(orderByField.replace('-', ''), orderByField.startsWith('-') ? 'desc' : 'asc'))
          : collection(db, entityName);
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      },
      filter: async (filters, orderByField, limitCount) => {
        let constraints = [];
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
             // Basic equality filter for now, can be expanded
             constraints.push(where(key, '==', value));
          });
        }
        if (orderByField) {
           constraints.push(firestoreOrderBy(orderByField.replace('-', ''), orderByField.startsWith('-') ? 'desc' : 'asc'));
        }
        if (limitCount) {
           constraints.push(firestoreLimit(limitCount));
        }

        const q = query(collection(db, entityName), ...constraints);
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      },
      get: async (id) => {
        const docRef = doc(db, entityName, id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          return { id: docSnap.id, ...docSnap.data() };
        } else {
          throw new Error('Not found');
        }
      },
      create: async (data) => {
        const docRef = await addDoc(collection(db, entityName), {
          ...data,
          createdAt: new Date().toISOString()
        });
        return { id: docRef.id, ...data };
      },
      update: async (id, data) => {
        const docRef = doc(db, entityName, id);
        await updateDoc(docRef, data);
        return { id, ...data };
      },
      delete: async (id) => {
        const docRef = doc(db, entityName, id);
        await deleteDoc(docRef);
        return { success: true };
      },
    });

    return new Proxy({}, {
      get: (_, entityName) => createEntityClient(entityName),
    });
  }
}

export const base44 = new FirebaseAdapter();
